import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";

import {
  FiCheckCircle,
  FiClock,
  FiXCircle,
  FiMessageSquare,
  FiUsers,
  FiImage,
  FiCheck,
  FiX,
  FiShield,
  FiTrash2,
} from "react-icons/fi";

import "./mm.css";

axios.defaults.withCredentials = true;

const API_URL =
  process.env.NODE_ENV === "development"
    ? "http://localhost:5000"
    : "https://api.yearbookhub.online";

function Memories() {
  const [activeTab, setActiveTab] = useState("students");
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
const [photoViewer, setPhotoViewer] = useState(null);
const [moderationModal, setModerationModal] = useState(null);
const [moderationNotice, setModerationNotice] = useState(null);
  /* =====================================================
     FETCH MEMORIES
  ===================================================== */

  const fetchMemories = useCallback(async () => {
    try {
      setLoading(true);

const res = await axios.get(
  `${API_URL}/api/admin/memories`,
  {
    withCredentials: true,
  }
);

      setMemories(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Fetch memories error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMemories();
  }, [fetchMemories]);


const handleDeleteSubmission = async (item) => {
  const confirmed = window.confirm(
    `Delete the submission from ${item.full_name || "this user"}?\n\nThis action cannot be undone.`
  );

  if (!confirmed) return;

  try {
    setProcessingId(item.id);
    setModerationNotice(null);

    await axios.delete(
      `${API_URL}/api/admin/memories/${item.id}`,
      { withCredentials: true }
    );

    setModerationNotice({
      type: "success",
      title: "Submission Deleted",
      message: "The submission was deleted successfully.",
    });

    await fetchMemories();
  } catch (err) {
    console.error("Delete submission error:", err);

    setModerationNotice({
      type: "error",
      title: "Delete Failed",
      message:
        err.response?.data?.message ||
        "Unable to delete this submission. Please try again.",
    });
  } finally {
    setProcessingId(null);
  }
};

  /* =====================================================
     REJECT
  ===================================================== */


const handleModeration = async () => {
  if (!moderationModal) return;

  const { id, action } = moderationModal;
  const isApprove = action === "approve";

  try {
    setProcessingId(id);
    setModerationModal(null);
    setModerationNotice(null);

    await axios.put(
      `${API_URL}/api/memories/${id}/${action}`,
      {},
      { withCredentials: true }
    );

    setModerationNotice({
      type: "success",
      title: isApprove ? "Submission Approved" : "Submission Rejected",
      message: isApprove
        ? "The submission has been approved successfully."
        : "The submission has been rejected successfully.",
    });

    await fetchMemories();
  } catch (err) {
    console.error(`${action} memory error:`, err);

    setModerationNotice({
      type: "error",
      title: "Action Failed",
      message:
        err.response?.data?.message ||
        `Failed to ${action} this submission. Please try again.`,
    });
  } finally {
    setProcessingId(null);
  }
};


  /* =====================================================
     STATISTICS
  ===================================================== */

  const stats = useMemo(() => {
    return {
      total: memories.length,

      pending: memories.filter(
        (item) => item.status === "pending"
      ).length,

      approved: memories.filter(
        (item) => item.status === "approved"
      ).length,

      rejected: memories.filter(
        (item) => item.status === "rejected"
      ).length,
    };
  }, [memories]);

  /* =====================================================
     STUDENT COUNT
  ===================================================== */

  const studentCount = useMemo(() => {
    return memories.filter((item) => {
      const type = String(
        item.type ||
          item.category ||
          item.role ||
          ""
      ).toLowerCase();

      return (
        type === "student" ||
        type === "students" ||
        !type
      );
    }).length;
  }, [memories]);

  /* =====================================================
     FACULTY COUNT
  ===================================================== */

  const facultyCount = useMemo(() => {
    return memories.filter((item) => {
      const type = String(
        item.type ||
          item.category ||
          item.role ||
          ""
      ).toLowerCase();

      return (
        type === "faculty" ||
        type === "faculty message" ||
        type === "faculty_messages"
      );
    }).length;
  }, [memories]);

  /* =====================================================
     FILTER
  ===================================================== */

  const filteredMemories = useMemo(() => {
    return memories.filter((item) => {
      const type = String(
        item.type ||
          item.category ||
          item.role ||
          ""
      ).toLowerCase();

      if (activeTab === "students") {
        return (
          type === "student" ||
          type === "students" ||
          !type
        );
      }

      return (
        type === "faculty" ||
        type === "faculty message" ||
        type === "faculty_messages"
      );
    });
  }, [memories, activeTab]);

  /* =====================================================
     STATUS CLASS
  ===================================================== */

  const getStatusClass = (status) => {
    if (status === "approved") {
      return "adssu-mm-status-approved";
    }

    if (status === "rejected") {
      return "adssu-mm-status-rejected";
    }

    return "adssu-mm-status-pending";
  };

  /* =====================================================
     STATUS ICON
  ===================================================== */

  const getStatusIcon = (status) => {
    if (status === "approved") {
      return <FiCheckCircle />;
    }

    if (status === "rejected") {
      return <FiXCircle />;
    }

    return <FiClock />;
  };

  /* =====================================================
     GET INITIALS
  ===================================================== */

  const getInitials = (name) => {
    if (!name) return "U";

    return name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0])
      .join("")
      .toUpperCase();
  };

  /* =====================================================
     PROFILE IMAGE
  ===================================================== */

  const getProfileImage = (profilePic) => {
    if (!profilePic) return null;

    if (
      profilePic.startsWith("http://") ||
      profilePic.startsWith("https://")
    ) {
      return profilePic;
    }

    return `https://api.yearbookhub.online${
      profilePic.startsWith("/")
        ? profilePic
        : `/${profilePic}`
    }`;
  };

  return (
    <div className="adssu-mm-page">

      {/* =================================================
          HEADER
      ================================================= */}

      <header className="adssu-mm-header">

        <div className="adssu-mm-header-left">

          <div className="adssu-mm-header-icon">
            <FiShield />
          </div>

          <div className="adssu-mm-header-content">

            <span className="adssu-mm-eyebrow">
              CONTENT MODERATION
            </span>

            <h1>
              Memories & Messages
            </h1>

            <p>
              Review and manage submissions before
              they appear in the digital yearbook.
            </p>

          </div>

        </div>

        <div className="adssu-mm-moderation-status">
          <span className="adssu-mm-status-dot" />

          <span>
            Moderation Center
          </span>
        </div>

      </header>

      {/* =================================================
          STATISTICS
      ================================================= */}

      <section className="adssu-mm-stats">

        <div className="adssu-mm-stat-card">

          <div className="adssu-mm-stat-icon adssu-mm-stat-total">
            <FiMessageSquare />
          </div>

          <div className="adssu-mm-stat-info">
            <span>
              Total Submissions
            </span>

            <strong>
              {stats.total}
            </strong>
          </div>

        </div>

        <div className="adssu-mm-stat-card">

          <div className="adssu-mm-stat-icon adssu-mm-stat-pending">
            <FiClock />
          </div>

          <div className="adssu-mm-stat-info">
            <span>
              Pending Review
            </span>

            <strong>
              {stats.pending}
            </strong>
          </div>

        </div>

        <div className="adssu-mm-stat-card">

          <div className="adssu-mm-stat-icon adssu-mm-stat-approved">
            <FiCheckCircle />
          </div>

          <div className="adssu-mm-stat-info">
            <span>
              Approved
            </span>

            <strong>
              {stats.approved}
            </strong>
          </div>

        </div>

        <div className="adssu-mm-stat-card">

          <div className="adssu-mm-stat-icon adssu-mm-stat-rejected">
            <FiXCircle />
          </div>

          <div className="adssu-mm-stat-info">
            <span>
              Rejected
            </span>

            <strong>
              {stats.rejected}
            </strong>
          </div>

        </div>

      </section>

      {/* =================================================
          REVIEW HEADER + TABS
      ================================================= */}

      <section className="adssu-mm-review-header">

        <div className="adssu-mm-review-title">

          <span className="adssu-mm-section-eyebrow">
            SUBMISSIONS
          </span>

          <h2>
            Review Center
          </h2>

          <p>
            Review submitted memories and faculty
            messages before publication.
          </p>

        </div>

        <div className="adssu-mm-tabs">

          <button
            type="button"
            className={`adssu-mm-tab ${
              activeTab === "students"
                ? "adssu-mm-tab-active"
                : ""
            }`}
            onClick={() => setActiveTab("students")}
          >
            <FiUsers />

            <span>
              Student Memories
            </span>

            <b>
              {studentCount}
            </b>
          </button>

          <button
            type="button"
            className={`adssu-mm-tab ${
              activeTab === "faculty"
                ? "adssu-mm-tab-active"
                : ""
            }`}
            onClick={() => setActiveTab("faculty")}
          >
            <FiMessageSquare />

            <span>
              Faculty Messages
            </span>

            <b>
              {facultyCount}
            </b>
          </button>

        </div>

      </section>

      {/* =================================================
          CONTENT
      ================================================= */}

      {loading ? (

        <div className="adssu-mm-empty">

          <div className="adssu-mm-loading-spinner" />

          <h3>
            Loading submissions...
          </h3>

          <p>
            Please wait while we retrieve the memories.
          </p>

        </div>

      ) : filteredMemories.length === 0 ? (

        <div className="adssu-mm-empty">

          <div className="adssu-mm-empty-icon">
            <FiMessageSquare />
          </div>

          <h3>
            No submissions found
          </h3>

          <p>
            There are currently no{" "}
            {activeTab === "students"
              ? "student memories"
              : "faculty messages"}{" "}
            to display.
          </p>

        </div>

      ) : (

        <div className="adssu-mm-list">

          {filteredMemories.map((item) => {

            const profileImage =
              getProfileImage(item.profile_pic);

            return (

              <article
                className="adssu-mm-card"
                key={item.id}
              >

                {/* =========================================
                    STUDENT PHOTO
                ========================================== */}

{activeTab === "students" &&
  Array.isArray(item.images) &&
  item.images.length > 0 && (
    <div className="adssu-mm-images-section">
      <div className="adssu-mm-images-header">
        <div className="adssu-mm-images-title">
          <FiImage />
          <span>
            Student Photos ({item.images.length})
          </span>
        </div>
      </div>

      <div className="adssu-mm-image-grid">
        {item.images.map((image, index) => (
          <div
            className="adssu-mm-image-wrapper"
            key={`${item.submission_id}-${index}`}
          >
            <img
  src={image}
  alt={`${item.full_name || "Student"} memory ${index + 1}`}
  className="adssu-mm-image"
  onClick={() =>
    setPhotoViewer({
      images: item.images,
      index,
    })
  }
  role="button"
  tabIndex={0}
  onKeyDown={(event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setPhotoViewer({
        images: item.images,
        index,
      });
    }
  }}
/>

            <div className="adssu-mm-image-label">
              <FiImage />
              Photo {index + 1}
            </div>
          </div>
        ))}
      </div>
    </div>
  )}

                {/* =========================================
                    CARD CONTENT
                ========================================== */}

                <div className="adssu-mm-card-content">

                  {/* TOP */}

                  <div className="adssu-mm-card-top">

                    <div className="adssu-mm-author">

                      <div className="adssu-mm-avatar">

                        {profileImage ? (

                          <img
                            src={profileImage}
                            alt={
                              item.full_name ||
                              "User"
                            }
                            onError={(e) => {
                              e.currentTarget.style.display =
                                "none";

                              const fallback =
                                e.currentTarget
                                  .parentElement
                                  .querySelector(
                                    ".adssu-mm-avatar-fallback"
                                  );

                              if (fallback) {
                                fallback.style.display =
                                  "flex";
                              }
                            }}
                          />

                        ) : null}

                        <div
                          className="adssu-mm-avatar-fallback"
                          style={{
                            display:
                              profileImage
                                ? "none"
                                : "flex",
                          }}
                        >
                          {getInitials(
                            item.full_name
                          )}
                        </div>

                      </div>

                      <div className="adssu-mm-author-info">

                        <h3>
                          {item.full_name ||
                            "Unknown User"}
                        </h3>

                        <div className="adssu-mm-author-meta">

                          <span className="adssu-mm-author-role">
                            {item.role === "faculty"
                              ? "Faculty"
                              : "Student"}
                          </span>

                          {item.department_program && (
                            <>
                              <span className="adssu-mm-meta-dot">
                                .
                              </span>

                              <span className="adssu-mm-author-department">
                                {item.department_program}
                              </span>
                            </>
                          )}

                        </div>

                      </div>

                    </div>

                    {/* STATUS */}

                    <div
                      className={`adssu-mm-status-badge ${getStatusClass(
                        item.status
                      )}`}
                    >
                      {getStatusIcon(item.status)}

                      <span>
                        {item.status || "pending"}
                      </span>
                    </div>

                  </div>

                  {/* MESSAGE */}

                 
{/* MESSAGE */}
{activeTab === "faculty" ? (
  <div className="admin-faculty-letter">
    {/* LETTERHEAD */}
    <div className="admin-faculty-letterhead">
      <div className="admin-faculty-seal">ADSSU</div>

      <div className="admin-faculty-university">
        <h3>AGUSAN DEL SUR STATE UNIVERSITY</h3>
        <p>DIGITAL YEARBOOK</p>
      </div>
    </div>

    <div className="admin-faculty-divider" />

    {/* MESSAGE DETAILS */}
    <div className="admin-faculty-meta">
      <span>FACULTY MESSAGE</span>
      <span>
        Submitted{" "}
        {item.created_at
          ? new Date(item.created_at).toLocaleDateString(
              "en-PH",
              {
                year: "numeric",
                month: "long",
                day: "numeric",
              }
            )
          : "Date unavailable"}
      </span>
    </div>

    {/* RECIPIENT */}
    <div className="admin-faculty-recipient">
      <small>TO</small>
      <strong>The Graduating Students</strong>
      <span>For the Yearbook</span>
    </div>

    {/* LETTER BODY */}
    <p className="admin-faculty-salutation">
      Dear Graduating Students,
    </p>

    <p className="admin-faculty-message">
      {item.message || "No message provided."}
    </p>

    {/* SIGNATURE */}
    <div className="admin-faculty-signature">
      <span>With my warmest regards,</span>
      <strong>{item.full_name || "Faculty Member"}</strong>
      <small>{item.department_program || "Faculty"}</small>
    </div>
  </div>
) : (
  <div className="adssu-mm-message-box">
    <div className="adssu-mm-message-icon">
      <FiMessageSquare />
    </div>

    <p>
      {item.message || "No message provided."}
    </p>
  </div>
)}


                   


{/* FOOTER */}
<div className="adssu-mm-card-footer">
  <span className="adssu-mm-submission-label">
    Submission #{item.id}
  </span>

  <div className="adssu-mm-actions">
    {/* Approve and Reject: pending submissions only */}
    {item.status === "pending" && (
      <>
        <button
          type="button"
          className="adssu-mm-approve-btn"
          onClick={() =>
            setModerationModal({
              id: item.id,
              action: "approve",
            })
          }
          disabled={processingId !== null}
        >
          <FiCheck />
          {processingId === item.id ? "Processing..." : "Approve"}
        </button>

        <button
          type="button"
          onClick={() =>
            setModerationModal({
              id: item.id,
              action: "reject",
            })
          }
          disabled={processingId !== null}
        >
          <FiX />
          Reject
        </button>
      </>
    )}

    {/* Delete: available for ALL submission statuses */}
    <button
      type="button"
      className="adssu-mm-delete-btn"
      onClick={() => handleDeleteSubmission(item)}
      disabled={processingId !== null}
    >
      <FiTrash2 />
      {processingId === item.id ? "Deleting..." : "Delete"}
    </button>
  </div>
</div>

                </div>

              </article>

            );
          })}

        </div>

      )}

      
{photoViewer && (
  <div
    className="adssu-mm-photo-viewer-overlay"
    onClick={() => setPhotoViewer(null)}
  >
    <div
      className="adssu-mm-photo-viewer"
      role="dialog"
      aria-modal="true"
      aria-label="Student memory photo viewer"
      onClick={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className="adssu-mm-photo-viewer-close"
        onClick={() => setPhotoViewer(null)}
        aria-label="Close photo viewer"
      >
        <FiX />
      </button>

      <button
        type="button"
        className="adssu-mm-photo-viewer-nav previous"
        onClick={() =>
          setPhotoViewer((current) => ({
            ...current,
            index:
              (current.index - 1 + current.images.length) %
              current.images.length,
          }))
        }
        aria-label="Previous photo"
      >
        ‹
      </button>

      <img
        className="adssu-mm-photo-viewer-image"
        src={photoViewer.images[photoViewer.index]}
        alt={`Student memory photo ${photoViewer.index + 1}`}
      />

      <button
        type="button"
        className="adssu-mm-photo-viewer-nav next"
        onClick={() =>
          setPhotoViewer((current) => ({
            ...current,
            index: (current.index + 1) % current.images.length,
          }))
        }
        aria-label="Next photo"
      >
        ›
      </button>

      <div className="adssu-mm-photo-viewer-counter">
        Photo {photoViewer.index + 1} of {photoViewer.images.length}
      </div>
    </div>
  </div>
)}


{moderationNotice && (
  <div
    className={`moderation-toast ${moderationNotice.type}`}
    role="status"
  >
    {moderationNotice.type === "success" ? (
      <FiCheckCircle />
    ) : (
      <FiXCircle />
    )}

    <div>
      <strong>{moderationNotice.title}</strong>
      <p>{moderationNotice.message}</p>
    </div>

    <button
      type="button"
      onClick={() => setModerationNotice(null)}
      aria-label="Close notification"
    >
      <FiX />
    </button>
  </div>
)}

{moderationModal && (
  <div
    className="moderation-modal-overlay"
    onClick={() => {
      if (processingId === null) setModerationModal(null);
    }}
  >
    <div
      className="moderation-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="moderation-modal-title"
      onClick={(event) => event.stopPropagation()}
    >
      <div
        className={`moderation-modal-icon ${
          moderationModal.action === "approve"
            ? "approve"
            : "reject"
        }`}
      >
        {moderationModal.action === "approve" ? (
          <FiCheckCircle />
        ) : (
          <FiXCircle />
        )}
      </div>

      <h2 id="moderation-modal-title">
        {moderationModal.action === "approve"
          ? "Approve Submission?"
          : "Reject Submission?"}
      </h2>

      <p>
        {moderationModal.action === "approve"
          ? "Are you sure you want to approve this submission for the digital yearbook?"
          : "Are you sure you want to reject this submission? It will not be approved for publication."}
      </p>

      <div className="moderation-modal-actions">
        <button
          type="button"
          className="moderation-cancel-btn"
          onClick={() => setModerationModal(null)}
        >
          Cancel
        </button>

        <button
          type="button"
          className={`moderation-confirm-btn ${
            moderationModal.action
          }`}
          onClick={handleModeration}
          disabled={processingId !== null}
        >
          {moderationModal.action === "approve"
            ? "Yes, Approve"
            : "Yes, Reject"}
        </button>
      </div>
    </div>
  </div>
)}


    </div>
  );
}

export default Memories;
